import { Injectable, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { ModalService } from '@ui-lib';
import { translateSignal } from '@jsverse/transloco';
import { Router } from '@angular/router';

/**
 * Service to handle deactivation confirmation modals
 * Provides a reusable way to show confirmation before navigation
 */
@Injectable({
  providedIn: 'root',
})
export class DeactivationConfirmationService {
  private readonly modalService = inject(ModalService);
  private readonly router = inject(Router);

  // titleKey is dynamic (route-dependent); updated in showConfirmationModal() before modal opens.
  private readonly titleKey = signal('');
  private readonly titleText = translateSignal(this.titleKey);
  private readonly messageText = translateSignal(
    'general_app_unsaved_data_warning_message'
  );

  /**
   * Shows a confirmation modal for unsaved changes
   * Determines context from current route automatically
   * @returns Observable that emits true if user confirms, false if they cancel
   */
  showConfirmationModal(): Observable<boolean> {
    // Get current route path
    const currentUrl = this.router.url;
    const isFundCreation = currentUrl.includes('fund-creation');

    this.titleKey.set(
      isFundCreation
        ? 'general_app_cancel_fund_confirmation_title'
        : 'general_app_cancel_confirmation_title'
    );

    return new Observable<boolean>(observer => {
      this.modalService.open({
        type: 'delete',
        title: this.titleText,
        message: this.messageText,
        buttons: [
          {
            id: 'keep',
            label: 'general_app_keep_button_label',
            variant: 'tertiary',
            onClick: () => {
              this.modalService.close();
              observer.next(false);
              observer.complete();
            },
          },
          {
            id: 'confirm',
            label: 'general_app_confirm_button_label',
            variant: 'primary',
            onClick: () => {
              this.modalService.close();
              observer.next(true);
              observer.complete();
            },
          },
        ],
      });
    });
  }
}
